import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatest,
  defer,
  fromEvent,
  merge,
  MonoTypeOperatorFunction,
  Observable,
  of,
  ReplaySubject,
  Subject,
  timer,
} from 'rxjs';
import {
  TabloFaceToPerson,
  TabloGroupElementConfig,
  TabloMember,
  TabloPageStructureUpdate,
  TabloPageBackgroundConfig,
  TabloPlaceHolder,
  TabloProjectStructure,
  TabloSinglePage,
  TabloTextConfig,
} from '@ui/tablo-generator/types';
import { ImageContainer } from '../../../../../../../../graphql/apps/photographer/src/graphql';
import {
  concatMap,
  debounceTime,
  delay,
  filter,
  first,
  map,
  mapTo,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs/operators';
import { ResizeSyncService } from '@ui/tablo-generator/resize-sync.service';
import { ZalamoObject } from '@ui/orders/graphql';
import { GetImageUrlService } from 'ZalamoLibrary/src/lib/core/image/image-loader/get-image-url.service';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { FontLoaderService } from '@ui/fonts-display/font-loader.service';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import { MoveableEventsParameters } from 'ngx-moveable';
import { ZalamoDialogService } from '@ui/zalamo-dialog/zalamo-dialog.service';
import { FaceToLabelPhotographerComponent } from '@ui/tablo-generator/tablo-studio/tablo-members-group/face-to-label-photographer/face-to-label-photographer.component';
import { TabloMembersGroupComponent } from '@ui/tablo-generator/tablo-studio/tablo-members-group/tablo-members-group.component';
import {
  firstValueFrom,
  fromResizeObserver,
} from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import {
  QueuedTabloPageStructureUpdate,
  TabloStructureUpdateQueue,
} from './structure-update-queue';
import { createTabloStructureSignature } from '../tablo-structure-signature';
import {
  areTabloPageBackgroundsEqual,
  createDefaultTabloPageBackground,
  normalizeTabloPageBackground,
} from '../tablo-background-transform';

function parseTransform(transform: string): {
  x: number;
  y: number;
  rotation: number;
} {
  // 1) Extract the matrix(...) part
  const matrixMatch = /matrix\(\s*([^)]+)\)/.exec(transform);
  // 2) Extract the translate(...) part
  const translateMatch = /translate\(\s*([^)]+)\)/.exec(transform);

  // Default values in case something is missing
  let a = 1,
    b = 0,
    c = 0,
    d = 1,
    e = 0,
    f = 0;
  let tx = 0,
    ty = 0;

  // Parse the matrix(...) if found
  if (matrixMatch) {
    // matrix(...) => a,b,c,d,e,f
    const nums = matrixMatch[1].split(',').map((val) => parseFloat(val.trim()));
    if (nums.length === 6) {
      [a, b, c, d, e, f] = nums;
    }
  }

  // Parse the translate(...) if found
  if (translateMatch) {
    // translate(...) => tx, ty
    // Note: might have 'px' suffix, so remove non-numerics except minus/period
    const nums = translateMatch[1]
      .split(',')
      .map((val) => parseFloat(val.replace(/[^\d\.\-]/g, '')));
    if (nums.length === 2) {
      [tx, ty] = nums;
    }
  }

  // ------------------------------------------------------------
  // CSS transforms are applied left-to-right. So "matrix(...) translate(...)"
  // means the final transform is T * M.
  //
  // If M = [ [a, c, e],
  //          [b, d, f],
  //          [0,  0, 1] ]
  // and T = [ [1, 0, tx],
  //          [0, 1, ty],
  //          [0, 0,  1] ],
  // then final = T * M = [ [a, c, e + tx],
  //                       [b, d, f + ty],
  //                       [0, 0,     1] ].
  //
  // So the final rotation is still determined by a,b,c,d,
  // but the final x,y translation = (e + tx, f + ty).
  // ------------------------------------------------------------

  const finalX = e + tx;
  const finalY = f + ty;

  // Extract rotation in radians => use atan2(b, a)
  const rotationRad = Math.atan2(b, a);
  // Convert to degrees
  const rotationDeg = rotationRad * (180 / Math.PI);

  return {
    x: finalX,
    y: finalY,
    rotation: rotationDeg,
  };
}

const untilDomQuiet = <T>(
  rootEl: Element,
  quietMs = 100,
): MonoTypeOperatorFunction<T> => {
  // Build (once) an Observable that emits *after each quiet period*.
  const domStable$ = defer(() => {
    return new Observable<void>((obs) => {
      // 1. Watch the subtree for any structural or attribute change.
      const mo = new MutationObserver(() => obs.next());
      mo.observe(rootEl, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });

      // 2. Emit immediately too, so the very first source value does not wait
      //    longer than needed.
      obs.next();

      // Disconnect when the stream is torn down.
      return () => mo.disconnect();
    }).pipe(debounceTime(quietMs)); // 3. Collapse bursts into one stable tick.
  });

  // 4. Gate every source value through the DOM-is-stable notifier.
  return (source) =>
    source.pipe(
      concatMap((value) =>
        domStable$.pipe(
          take(1), // wait for the next quiet tick
          mapTo(value), // then release the held value
        ),
      ),
    );
};
const extractImageUrlValue = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    const urlValue = Object.values(value as Record<string, unknown>).find(
      (entry) => typeof entry === 'string',
    );
    return typeof urlValue === 'string' ? urlValue : null;
  }

  return null;
};
const isStructureItemVisible = (value: {
  hidden?: boolean;
  config?: { hidden?: boolean };
}) => !value.hidden && !value.config?.hidden;
const snapTransform = (el: HTMLElement, snapDeg = 1.5) => {
  // 1. Get the final 2-D matrix the browser is using right now
  const computed = getComputedStyle(el).transform;
  if (!computed || computed === 'none') return; // no transform at all

  // 2. Decompose the matrix to the current absolute angle
  //     DOMMatrix handles 'matrix(...)', 'matrix3d(...)', and even a list
  const m = new DOMMatrix(computed);
  const curr = Math.atan2(m.b, m.a) * (180 / Math.PI); // degrees
  const norm = ((curr % 360) + 360) % 360; // [0,360)

  // 3. Find the closest cardinal if we are inside the magnet zone
  const card = [0, 90, 180, 270];
  let target = norm;
  for (const c of card) {
    const diff = Math.min(Math.abs(norm - c), 360 - Math.abs(norm - c));
    if (diff < snapDeg) {
      target = c;
      break;
    }
  }

  // 4. Already on-grid? Nothing to do.
  if (target === norm) return;

  // 5. Compute the delta we still need and add one extra rotate()
  const delta = target - norm; // may be negative
  el.style.transform = `${el.style.transform || computed} rotate(${delta}deg)`;
};
const alignSpine = (
  container: HTMLElement,
  sub: HTMLElement,
  spine: HTMLElement,
  scale: number,
): string => {
  if (!container || !sub || !spine) return;

  // 1.  Get rectangles **after** CSS transforms.
  const cRect = (container.firstChild as HTMLElement).getBoundingClientRect();
  const sRect = spine.getBoundingClientRect();

  // 2.  Compute desired and current X positions.
  const containerCentreX = cRect.left + cRect.width / 2; // visual centre
  const spineRightX = sRect.right; // visual right edge
  let delta = (containerCentreX - spineRightX) / scale;

  // 3. Extract any existing translate from the sub-container transform.
  const currentMatrix = new DOMMatrixReadOnly(
    getComputedStyle(sub).transform || 'matrix(1,0,0,1,0,0)',
  );
  const currentTX = currentMatrix.m41; // existing translateX
  const currentTY = currentMatrix.m42; // existing translateY

  // if subcontainer will be moved outside of the container, we need to move it back
  // into the container
  const containerLeftX = cRect.left;
  const containerRightX = cRect.right;
  const subRect = sub.getBoundingClientRect();
  if (subRect.right + delta * scale > containerRightX) {
    delta = 0;
  } else if (subRect.left + delta * scale < containerLeftX) {
    delta = 0;
  }

  const newTX = currentTX + delta;
  return `translate(${newTX}px, ${currentTY}px)`;
};

type TabloCanvasPage = TabloSinglePage & {
  structure?: TabloProjectStructure;
};

type TabloCanvasTemplate = {
  width: number;
  height: number;
  pages: TabloCanvasPage[];
  faceMatchConfig: Record<string, TabloFaceToPerson>;
  type?: string;
};

type PendingBackgroundTransformUpdate = {
  page: number;
  background: TabloPageBackgroundConfig;
};

const MIN_BACKGROUND_PAN_PADDING = 40;
const TABLO_STRUCTURE_UPDATE_DEBOUNCE_MS = 100;
@Component({
    selector: 'lib-tablo-canva',
    templateUrl: './tablo-canva.component.html',
    styleUrls: ['./tablo-canva.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TabloCanvaComponent
  extends AliveState
  implements OnInit, OnDestroy
{
  @Input() context: ZalamoObject;
  @Input() contextId: string;
  @Input() isTemplate = false;
  @Input() @Required publicSessionOrganizationId: string;
  @Input() @Required publicSessionOrganizationGroupId: string;
  @Input() publicSessionSignupCampaignId: string | null = null;
  @Input() isMockup = false;
  currentTemplate$ = new BehaviorSubject<TabloCanvasTemplate | null>(null);
  currentPage$ = new BehaviorSubject<number>(0);
  private structureUpdateFromTop = false;
  private structurePageContext = 0;
  private structureSessionId = 0;
  private readonly structureUpdateQueue = new TabloStructureUpdateQueue(
    (update) => this.structureUpdated.emit(update),
    TABLO_STRUCTURE_UPDATE_DEBOUNCE_MS,
  );
  @HostBinding('class.printMode') @Input() printMode = false;
  @HostBinding('class.hasSpine') @Input() hasSpine = false;
  @HostBinding('class.mightHaveSpine') @Input() mightHaveSpine = false;
  @Output() enableMoveByDragAndDrop = new EventEmitter<boolean>();
  @Output() editCrop = new EventEmitter<TabloMember>();
  @Output() photosAssigned = new EventEmitter<
    Record<string, TabloFaceToPerson>
  >();
  @Output() moveToTop = new EventEmitter<{
    id: string;
    currentIndex: number;
  }>();
  @Output() overflowError = new EventEmitter<{
    page: number;
    id: string;
    hasError: boolean;
  }>();
  @Output() moveToBottom = new EventEmitter<{
    id: string;
    currentIndex: number;
  }>();
  @Output() moveUp = new EventEmitter<{ id: string; currentIndex: number }>();
  @Output() moveDown = new EventEmitter<{ id: string; currentIndex: number }>();

  showAskForPhotos$ = new Subject();
  @Input() set askForFaceMatch(ask) {
    if (ask) {
      this.showAskForPhotos$.next(ask);
    }
  }
  tabloFaceToPerson$ = new BehaviorSubject<Record<string, TabloFaceToPerson>>(
    null,
  );
  @Output() removeItem = new EventEmitter<string>();
  @Input() set template(details: TabloCanvasTemplate | null) {
    if (!details) {
      return;
    }
    // console.log(details);
    this.currentTemplate$.next(details);

    this.calculateMineSize(details.width, details.height);
    this.width$.next(details.width);
    this.height$.next(details.height);
    this.tabloFaceToPerson$.next(details.faceMatchConfig);
    this.syncBackgroundTransformFromTemplate();
  }
  @Output() showGraphic = new EventEmitter<string>();
  @Output() hideGraphic = new EventEmitter<string>();
  @Input() set hideControls(hide: any) {
    this.setHoverOver(null);
  }
  hoverOver = new BehaviorSubject<string | null>(null);
  @Output() openEditMode = new EventEmitter<TabloProjectStructure>();
  sourceGroups$ = new BehaviorSubject<ImageContainer[]>([]);
  @Output() closeEditMode = new EventEmitter<void>();

  @Input() set photoSourceGroups(groups: ImageContainer[]) {
    this.sourceGroups$.next(groups);
  }

  @Input() @Required photographerLogo;
  @Output() scrollZoomUpdated = new EventEmitter<{
    scale: number;
    x: number;
    y: number;
  }>();
  @Output() backgroundTransformUpdated = new EventEmitter<{
    page: number;
    background: TabloPageBackgroundConfig;
    syncTemplate?: boolean;
  }>();
  elementResized = new Subject<string>();
  structure$ = new BehaviorSubject<TabloProjectStructure>([]);
  visibleStructure$ = this.structure$.pipe(
    map((structure) => structure.filter(isStructureItemVisible)),
  );
  updateWatchers: Record<string, Subject<void>> = {};
  @Output() structureUpdated = new EventEmitter<TabloPageStructureUpdate>();
  @Input() structureAlreadySet = false;
  structureSet = false;
  elementsToLoad = {
    text: 0,
    groups: 0,
    background: 0,
  };
  elementsLoaded = {
    text: 0,
    groups: 0,
    background: 0,
  };
  @Output() isCanvasReady = new EventEmitter<void>();
  @ViewChildren(TabloMembersGroupComponent)
  private memberGroups: QueryList<TabloMembersGroupComponent>;
  structureUpdatedFromTopAfterChange = false;
  private spineAlignmentRunId = 0;
  private canvasReadyEmitted = false;
  private loadedGroupElementIds = new Set<string>();
  @Input() set structure(structure: TabloProjectStructure) {
    if (!structure) {
      return;
    }

    const pageContext = this.currentPage$.value;
    const nextStructure = this.getStructureWithDefaultRotation(structure);

    if (
      this.structureAlreadySet &&
      this.structurePageContext === pageContext &&
      createTabloStructureSignature(this.structure$.value) ===
        createTabloStructureSignature(nextStructure)
    ) {
      return;
    }

    this.structureUpdateQueue.flushPending();
    this.structurePageContext = pageContext;
    this.structureSessionId += 1;
    this.elementsLoaded = {
      text: 0,
      groups: 0,
      background: 0,
    };
    this.loadedGroupElementIds = new Set<string>();
    this.canvasReadyEmitted = false;

    this.structureUpdateFromTop = true;
    this.structure$.next(nextStructure);
    const visibleStructure = structure.filter(isStructureItemVisible);
    this.elementsToLoad.text = visibleStructure.filter(
      (v) => v.type === 'text',
    ).length;
    this.elementsToLoad.groups = visibleStructure.filter(
      (v) => v.type === 'group' && !v.config.config.isNameOnly,
    ).length;
    this.structureUpdatedFromTopAfterChange = true;
    this.structureUpdateFromTop = false;
    this.structureAlreadySet = true;
    this.scheduleSpineAlignmentAfterStructureChange();
  }

  private getStructureWithDefaultRotation(
    structure: TabloProjectStructure,
  ): TabloProjectStructure {
    return structure.map((element) => {
      const config = element.config as typeof element.config & {
        rotation?: number;
      };

      return {
        ...element,
        config: {
          ...config,
          rotation: config.rotation ?? 0,
        },
      };
    }) as TabloProjectStructure;
  }

  @Input() set updatedScale(scale: number) {
    this.scale.next(scale);
  }
  @ViewChild('backgroundImage', { static: false })
  backgroundImage: ElementRef<HTMLImageElement>;

  @Input() leadingChildId: string = null;
  @Input() disabled = false;
  elementSnapDirections = {
    top: true,
    left: true,
    bottom: true,
    right: true,
    center: true,
    middle: true,
  };
  scale = new BehaviorSubject(1);
  @Output() minScaleSet = new EventEmitter<number>();
  width$ = new BehaviorSubject(0);
  height$ = new BehaviorSubject(0);
  @ViewChild('content', { static: true })
  contentContainer: ElementRef<HTMLElement>;
  $x = new BehaviorSubject(0);
  $y = new BehaviorSubject(0);

  translateX$ = new BehaviorSubject(0);
  translateY$ = new BehaviorSubject(0);
  currentX$ = combineLatest([this.$x, this.translateX$]).pipe(
    map(([x, translateX]) => x + translateX),
  );
  currentY$ = combineLatest([this.$y, this.translateY$]).pipe(
    map(([y, translateY]) => y + translateY),
  );
  @Required @Input() projectId!: string;

  @Input() set translated({ x, y }) {
    this.translateX$.next(x);
    this.translateY$.next(y);
  }

  minScaleAvailable = 0;
  scaleUpdated$ = merge(
    this.scale.pipe(mapTo(true)),
    this.scale.pipe(delay(500), mapTo(false)),
  ).pipe(startWith(false));

  @Input() set currentPage(page: number) {
    if (typeof page !== 'number') {
      return;
    }
    this.flushPendingBackgroundTransformSync();
    this.finishBackgroundEdit();
    this.canvasReadyEmitted = false;
    this.structureAlreadySet = false;
    this.currentPage$.next(page);
    this.syncBackgroundTransformFromTemplate();
  }
  lastImageUrl = null;
  imageUrl$ = combineLatest([this.currentTemplate$, this.currentPage$]).pipe(
    switchMap(([template, page]) => {
      if (!template) {
        return of(null);
      }
      if (template.pages[page].url) {
        this.elementsToLoad.background = 1;
      } else {
        this.elementsToLoad.background = 0;
      }
      return this.getImageUrlService.transform(
        {
          photoId: template.pages[page].url,
        },
        `${this.projectId}-assets`,
        'photographer',
        ZalamoObject.DesignerAsset,
        this.projectId,
        5000,
      );
    }),
    tap((x) => console.log(x)),

    shareReplay(1),
  );
  readonly isShiftDown$ = new BehaviorSubject(false);
  readonly backgroundTransform$ =
    new BehaviorSubject<TabloPageBackgroundConfig>(
      createDefaultTabloPageBackground(),
    );
  readonly backgroundTransformStyle$ = this.backgroundTransform$.pipe(
    map(({ x, y, scale }) => `matrix(${scale}, 0, 0, ${scale}, ${x}, ${y})`),
  );

  readonly snapDistFormat = (dist: number): string =>
    `${Math.round(dist / this.scale.value)}px`;

  frame = { translate: [0, 0], rotate: 0 };
  private readonly destroy$ = new Subject<void>();
  private readonly backgroundTransformSync$ =
    new Subject<PendingBackgroundTransformUpdate>();
  private pendingBackgroundTransformSync: PendingBackgroundTransformUpdate | null =
    null;
  private dndInProgress = false;
  isBackgroundDragging = false;
  private readonly backgroundDragState = {
    active: false,
    pointerId: null as number | null,
    startPointerX: 0,
    startPointerY: 0,
    startX: 0,
    startY: 0,
    hasChanges: false,
  };
  private readonly backgroundMaxScale = 5;

  private setHoverOver(id: string | null): void {
    if (this.hoverOver.value === id) {
      return;
    }

    this.hoverOver.next(id);
  }

  constructor(
    private elementRef: ElementRef,
    private resizeSyncService: ResizeSyncService,
    private getImageUrlService: GetImageUrlService,
    private fontLoaderService: FontLoaderService,
    private organizationService: OrganizationService,
    private zalamoDialogService: ZalamoDialogService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  @HostListener('window:keydown', ['$event'])
  handleWindowKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Shift') {
      return;
    }

    this.syncBackgroundEditModifier(true);
  }

  @HostListener('window:keyup', ['$event'])
  handleWindowKeyUp(event: KeyboardEvent): void {
    if (event.key !== 'Shift') {
      return;
    }

    this.syncBackgroundEditModifier(false);
  }

  @HostListener('window:blur')
  handleWindowBlur(): void {
    this.syncBackgroundEditModifier(false);
    this.dndInProgress = false;
    this.disableMoveByDragAndDrop();
  }

  @HostListener('window:pointerup', ['$event'])
  handleWindowPointerUp(event: PointerEvent): void {
    this.dndInProgress = false;
    this.disableMoveByDragAndDrop();
    this.finishBackgroundEdit(event);
  }

  @HostListener('window:pointercancel', ['$event'])
  handleWindowPointerCancel(event: PointerEvent): void {
    this.dndInProgress = false;
    this.disableMoveByDragAndDrop();
    this.finishBackgroundEdit(event);
  }

  ngOnInit(): void {
    // this.resizeSyncService.sizeUpdated.subscribe((id) => {
    //   console.log(id);
    // });

    this.zone.runOutsideAngular(() => {
      fromEvent<WheelEvent>(this.contentContainer.nativeElement, 'wheel', {
        passive: false,
      })
        // emit **once per animation frame**
        .pipe(
          throttleTime(0, animationFrameScheduler),
          takeUntil(this.destroy$),
        )
        .subscribe((ev) => {
          // Heavy math / state changes happen here OUTSIDE the zone
          this.onWheel(ev);

          // Only now schedule Angular to render, max 60 fps
          this.zone.run(() => this.cdr.markForCheck());
        });
      fromEvent<PointerEvent>(
        this.contentContainer.nativeElement,
        'pointermove',
        {
          passive: true,
        },
      )
        // emit **once per animation frame**
        .pipe(
          throttleTime(0, animationFrameScheduler),
          takeUntil(this.destroy$),
        )
        .subscribe((ev) => this.calculateElementsWithHover(ev));
    });
    this.subscribeWhileAlive(
      this.backgroundTransformSync$.pipe(
        debounceTime(150),
        tap((update) => {
          if (this.pendingBackgroundTransformSync !== update) {
            return;
          }

          this.pendingBackgroundTransformSync = null;
          this.emitBackgroundTransform(update, true);
        }),
      ),
      timer(0, 2000).pipe(
        tap(() => {
          this.checkIfAllLoaded();
        }),
      ),
      this.zalamoDialogService
        .watchIfAnythingOpen()
        .pipe(tap(() => this.setHoverOver(null))),
      fromResizeObserver(this.elementRef.nativeElement).pipe(
        filter(() => !!this.currentTemplate$.value),
        debounceTime(50),
        tap(() => {
          this.calculateMineSize(
            this.currentTemplate$.value.width,
            this.currentTemplate$.value.height,
          );
        }),
      ),
      this.showAskForPhotos$.pipe(
        takeUntil(this.destroy$),
        debounceTime(1000),
        tap((ask) => {
          this.assignPhotos(ask);
        }),
      ),
      this.structure$.pipe(
        filter(() => this.structureAlreadySet),
        tap(async (structure) => {
          await this.organizationService
            .getCurrentOrganization()
            .pipe(
              first(),
              switchMap((organizationId) =>
                Promise.all(
                  structure
                    .filter((v) => v.type === 'text')
                    .map((v) =>
                      this.fontLoaderService
                        .loadFont(
                          (v.config as TabloTextConfig).config.font.fontId,
                          (v.config as TabloTextConfig).config.font.fileName,
                          organizationId,
                        )
                        .pipe(first())
                        .toPromise(),
                    ),
                ),
              ),
            )
            .toPromise();
        }),
        filter(() => !this.structureUpdateFromTop),
        tap((newStructure) => {
          this.enqueueStructureUpdate(newStructure);
        }),
      ),
    );
  }

  private enqueueStructureUpdate(structure: TabloProjectStructure): void {
    const update: QueuedTabloPageStructureUpdate = {
      page: this.structurePageContext,
      structure,
      sessionId: this.structureSessionId,
    };

    this.structureUpdateQueue.enqueue(update);
  }

  calculateMineSize(projectWidth: number, projectHeight: number) {
    if (this.printMode) {
      this.scale.next(1);
      this.minScaleSet.next(1);
      return;
    }
    const element = this.elementRef.nativeElement as HTMLElement;
    const { width, height } = element.getBoundingClientRect();

    const widthScale = (width - 40) / projectWidth;
    const heightScale = (height - 80) / projectHeight;

    const scale = Math.min(widthScale, heightScale);

    // These values center the project inside the container
    const offsetX = (width - 40 - projectWidth * scale) / 2;
    const offsetY = (height - 40 - projectHeight * scale) / 2;

    // Then set them regardless of ratio
    this.minScaleAvailable = scale;
    this.$x.next(offsetX);
    this.$y.next(offsetY);
    this.minScaleSet.next(scale);
    this.scale.next(scale);
  }

  onDrag({ target, beforeTranslate, transform }, item?: any) {
    // this.frame.translate = beforeTranslate;
    this.dndInProgress = true;
    this.frame.translate = beforeTranslate;
    target.style.transform = transform;
  }

  async onDragEnd(e, item) {
    this.dndInProgress = false;
    this.setHoverOver(item.id);
    if (e.lastEvent) {
      const currentStructure = this.structure$.getValue();
      const index = currentStructure.findIndex((x) => x.id === item.id);
      if (index === -1) {
        return;
      }

      currentStructure[index].config.transform = e.target.style.transform;
      if (item.type === 'group' && item.config.config.addSpine) {
        await this.alignGroupSpineInStructure(currentStructure, item.id);
      }

      this.structureUpdatedFromTopAfterChange = false;
      this.structure$.next([...currentStructure]);
      this.refreshMoveableAfterDomUpdate(e.moveable);
    }
  }

  private refreshMoveableAfterDomUpdate(moveable?: {
    updateRect(): void;
    updateTarget(): void;
  }): void {
    if (!moveable) {
      return;
    }

    const scheduleRefresh = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          moveable.updateRect();
          moveable.updateTarget();
        });
      });
    };

    if (this.zone?.runOutsideAngular) {
      this.zone.runOutsideAngular(scheduleRefresh);
      return;
    }

    scheduleRefresh();
  }

  private scheduleSpineAlignmentAfterStructureChange(): void {
    const runId = ++this.spineAlignmentRunId;
    void Promise.resolve().then(() =>
      this.alignSpinesAfterStructureChange(runId),
    );
  }

  private async waitForGroupElementDomQuiet(
    itemId: string,
    shouldAbort?: () => boolean,
  ): Promise<HTMLElement | null> {
    await firstValueFrom(timer(0));
    if (shouldAbort?.()) {
      return null;
    }

    const element = document.getElementById(itemId) as HTMLElement | null;
    if (!element) {
      return null;
    }

    await firstValueFrom(of(null).pipe(untilDomQuiet(element, 100)));
    if (shouldAbort?.()) {
      return null;
    }

    return (document.getElementById(itemId) as HTMLElement | null) ?? element;
  }

  private async alignGroupSpineInStructure(
    structure: TabloProjectStructure,
    itemId: string,
    shouldAbort?: () => boolean,
  ): Promise<boolean> {
    const element = await this.waitForGroupElementDomQuiet(itemId, shouldAbort);
    if (!element || shouldAbort?.()) {
      return false;
    }

    const firstSpineElement = element.querySelector('.spine') as HTMLElement;
    const alignedTransform = alignSpine(
      this.elementRef.nativeElement,
      element,
      firstSpineElement,
      this.scale.value,
    );
    const index = structure.findIndex((candidate) => candidate.id === itemId);
    if (
      index === -1 ||
      !alignedTransform ||
      structure[index].config.transform === alignedTransform
    ) {
      return false;
    }

    structure[index].config.transform = alignedTransform;
    return true;
  }

  private async alignSpinesAfterStructureChange(runId: number): Promise<void> {
    const structure = this.structure$.getValue();
    const groupsWithSpine = structure.filter(
      (item) => item.type === 'group' && item.config.config.addSpine,
    );
    if (!groupsWithSpine.length) {
      return;
    }
    const nextStructure = [...this.structure$.getValue()];
    let hasChanges = false;
    for (const item of groupsWithSpine) {
      if (
        await this.alignGroupSpineInStructure(
          nextStructure,
          item.id,
          () => runId !== this.spineAlignmentRunId,
        )
      ) {
        hasChanges = true;
      }
    }
    if (!hasChanges || runId !== this.spineAlignmentRunId) {
      return;
    }
    this.structureUpdatedFromTopAfterChange = false;
    this.structure$.next([...nextStructure]);
  }

  async onResizeEnd(e, item) {
    this.dndInProgress = false;
    this.setHoverOver(item.id);
    const currentStructure = this.structure$.getValue();
    const index = currentStructure.findIndex((x) => x.id === item.id);
    currentStructure[index].config.width = Math.max(e.lastEvent.width, 10);
    currentStructure[index].config.height = Math.max(e.lastEvent.height, 10);
    currentStructure[index].config.transform = e.target.style.transform;

    this.structure$.next([...currentStructure]);
    e.target.style.transform = `rotate(${item.rotation}deg)`;

    this.elementResized.next(item.id);
    if (item.type === 'group' && item.config.config.addSpine) {
      if (await this.alignGroupSpineInStructure(currentStructure, item.id)) {
        this.structure$.next([...currentStructure]);
      }
    }

    this.refreshMoveableAfterDomUpdate(e.moveable);
  }

  onResizeStart({ target, set, setOrigin, dragStart }) {
    // Set origin if transform-origin use %.
    setOrigin(['%', '%']);

    // If cssSize and offsetSize are different, set cssSize. (no box-sizing)
    const style = window.getComputedStyle(target);
    const cssWidth = parseFloat(style.width);
    const cssHeight = parseFloat(style.height);
    set([cssWidth, cssHeight]);

    // If a drag event has already occurred, there is no dragStart.
    const { x, y } = parseTransform(
      target.style.transform || style.transform || '',
    );
    this.frame.translate = [x, y];
    dragStart && dragStart.set(this.frame.translate);
  }

  pointerLeft() {
    console.log('pointer left');
  }
  pointerEnter() {
    console.log('pointer enter');
  }

  onResize({ target, width, height, drag }, item?: any) {
    this.dndInProgress = true;
    this.resizeSyncService.sizeUpdated.next(item?.id);
    target.style.width = `${Math.max(width, 10)}px`;
    target.style.height = `${Math.max(height, 10)}px`;

    // get drag event
    target.style.transform = drag.transform;
  }

  trackGroup(index: number, item: any) {
    return item.id;
  }

  placeholdersUpdated($event: TabloPlaceHolder[], item: any) {
    const currentStructure = this.structure$.getValue();
    const index = currentStructure.findIndex((x) => x.id === item.id);
    if (currentStructure[index].type === 'group') {
      // @ts-ignore
      currentStructure[index].config.placeholders = $event ?? [];
    }

    this.structure$.next([...currentStructure]);
  }

  handPickUpdated(
    $event: Record<
      string,
      { photoId: string; crop: [number, number, number, number] }
    >,
    item: any,
  ) {
    const currentStructure = this.structure$.getValue();
    const index = currentStructure.findIndex((x) => x.id === item.id);
    if (currentStructure[index].type === 'group') {
      // @ts-ignore
      currentStructure[index].config.handPicked = $event;
    }

    this.structure$.next([...currentStructure]);
  }

  groupMembersLoaded(groupElementId: string) {
    if (this.loadedGroupElementIds.has(groupElementId)) {
      return;
    }

    this.loadedGroupElementIds.add(groupElementId);
    this.elementsLoaded.groups = this.loadedGroupElementIds.size;
    this.checkIfAllLoaded();
  }

  backgroundLoaded() {
    console.log(`

      *****************************
      background loaded
      *****************************

    `);
    this.elementsLoaded.background++;
    this.checkIfAllLoaded();
  }

  async checkIfAllLoaded() {
    if (this.canvasReadyEmitted) {
      return;
    }
    if (
      this.elementsLoaded.text >= this.elementsToLoad.text &&
      this.elementsLoaded.groups >= this.elementsToLoad.groups &&
      (this.elementsLoaded.background >= this.elementsToLoad.background ||
        (this.elementsToLoad.background &&
          (await firstValueFrom(this.imageUrl$).then((v) =>
            extractImageUrlValue(v),
          )) === this.backgroundImage?.nativeElement.src &&
          this.backgroundImage?.nativeElement.naturalWidth > 0))
    ) {
      this.canvasReadyEmitted = true;
      this.isCanvasReady.emit();
    }
  }

  textLoaded() {
    this.elementsLoaded.text++;
    this.checkIfAllLoaded();
  }

  getRenderDiagnostics(): Record<
    string,
    string | number | boolean | null | string[]
  > {
    const groups = this.getPendingGroupImageDebugDetails();
    const missingImages = groups.flatMap((group) => group.missingImages);
    return {
      canvasReady: this.canvasReadyEmitted,
      expectedBackgrounds: this.elementsToLoad.background,
      loadedBackgrounds: this.elementsLoaded.background,
      expectedGroups: this.elementsToLoad.groups,
      loadedGroups: this.elementsLoaded.groups,
      expectedTexts: this.elementsToLoad.text,
      loadedTexts: this.elementsLoaded.text,
      pendingGroupIds: groups
        .map((group) => group.groupElementId)
        .filter((id): id is string => !!id)
        .slice(0, 50),
      missingGroupImageCount: missingImages.length,
      missingPhotoIds: missingImages
        .map((image) => image.photoId)
        .filter((id): id is string => !!id)
        .slice(0, 50),
      backgroundPhotoId:
        this.currentTemplate$.value?.pages?.[this.currentPage$.value]?.url ||
        null,
    };
  }

  private getPendingGroupImageDebugDetails() {
    return (
      this.memberGroups
        ?.toArray()
        .map((group) => group.getMissingImageDebugDetails())
        .filter(
          (group) =>
            group.loadedImages !== group.expectedImages ||
            group.missingImages.length > 0,
        ) ?? []
    );
  }

  private syncBackgroundTransformFromTemplate(): void {
    const page = this.currentTemplate$.value?.pages?.[this.currentPage$.value];
    this.backgroundTransform$.next(
      this.clampBackgroundTransform(
        normalizeTabloPageBackground(page?.background),
      ),
    );
  }

  private hasBackgroundImage(): boolean {
    return !!this.currentTemplate$.value?.pages?.[this.currentPage$.value]?.url;
  }

  private syncBackgroundEditModifier(isShiftPressed: boolean): void {
    if (isShiftPressed) {
      if (!this.isShiftDown$.value) {
        this.setHoverOver(null);
        this.isShiftDown$.next(true);
      }
      return;
    }

    if (!this.isShiftDown$.value) {
      return;
    }

    this.isShiftDown$.next(false);
    this.finishBackgroundEdit();
  }

  private getMinimumBackgroundScale(): number {
    const width = this.width$.value;
    const height = this.height$.value;
    const minDimension = Math.min(width, height);

    if (!minDimension) {
      return 1.05;
    }

    return 1 + (2 * MIN_BACKGROUND_PAN_PADDING) / minDimension;
  }

  private clampBackgroundTransform(
    background: TabloPageBackgroundConfig,
  ): TabloPageBackgroundConfig {
    const width = this.width$.value;
    const height = this.height$.value;
    const minScale = this.getMinimumBackgroundScale();
    const scale = Math.max(
      minScale,
      Math.min(this.backgroundMaxScale, background.scale || 1),
    );

    if (!width || !height) {
      return {
        x: background.x ?? 0,
        y: background.y ?? 0,
        scale,
      };
    }

    const maxOffsetX = (width * (scale - 1)) / 2;
    const maxOffsetY = (height * (scale - 1)) / 2;

    return {
      x: Math.max(-maxOffsetX, Math.min(maxOffsetX, background.x ?? 0)),
      y: Math.max(-maxOffsetY, Math.min(maxOffsetY, background.y ?? 0)),
      scale,
    };
  }

  private emitBackgroundTransform(
    update: PendingBackgroundTransformUpdate = {
      page: this.currentPage$.value,
      background: this.backgroundTransform$.value,
    },
    syncTemplate = false,
  ): void {
    this.backgroundTransformUpdated.emit({
      page: update.page,
      background: {
        ...update.background,
      },
      syncTemplate,
    });
  }

  private queueBackgroundTransformSync(
    background: TabloPageBackgroundConfig,
  ): void {
    const update: PendingBackgroundTransformUpdate = {
      page: this.currentPage$.value,
      background: {
        ...background,
      },
    };

    this.pendingBackgroundTransformSync = update;
    this.backgroundTransformSync$.next(update);
  }

  private flushPendingBackgroundTransformSync(): void {
    const update = this.pendingBackgroundTransformSync;
    if (!update) {
      return;
    }

    this.pendingBackgroundTransformSync = null;
    this.emitBackgroundTransform(update, true);
  }

  private updateBackgroundTransform(
    background: TabloPageBackgroundConfig,
    scheduleTemplateSync = false,
  ): boolean {
    const nextBackground = this.clampBackgroundTransform(background);
    if (
      areTabloPageBackgroundsEqual(
        this.backgroundTransform$.value,
        nextBackground,
      )
    ) {
      return false;
    }

    this.backgroundTransform$.next(nextBackground);
    if (scheduleTemplateSync) {
      this.queueBackgroundTransformSync(nextBackground);
    }

    return true;
  }

  private zoomBackground(event: WheelEvent): void {
    const rect = this.contentContainer.nativeElement.getBoundingClientRect();
    const canvasScale = this.scale.value || 1;
    const pointerX = (event.clientX - rect.left) / canvasScale;
    const pointerY = (event.clientY - rect.top) / canvasScale;
    const previous = this.backgroundTransform$.value;
    const centerX = this.width$.value / 2;
    const centerY = this.height$.value / 2;
    const contentX =
      centerX + (pointerX - centerX - previous.x) / previous.scale;
    const contentY =
      centerY + (pointerY - centerY - previous.y) / previous.scale;
    let nextScale = previous.scale - event.deltaY * 0.0015;

    nextScale = Math.max(
      this.getMinimumBackgroundScale(),
      Math.min(this.backgroundMaxScale, nextScale),
    );

    this.updateBackgroundTransform(
      {
        x: pointerX - centerX - nextScale * (contentX - centerX),
        y: pointerY - centerY - nextScale * (contentY - centerY),
        scale: nextScale,
      },
      true,
    );
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();
    this.syncBackgroundEditModifier(event.shiftKey);

    if (event.shiftKey && this.hasBackgroundImage()) {
      this.zoomBackground(event);
      return;
    }

    // 1) Get bounding rect to know mouse position relative to .content
    const rect =
      this.contentContainer.nativeElement.parentElement.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;

    // 2) Current scale and combined offsets
    const oldScale = this.scale.value;
    const offsetX = this.$x.value + this.translateX$.value;
    const offsetY = this.$y.value + this.translateY$.value;

    // Convert pointer from screen coords to *content* coords
    //   cx = (pointerX - offsetX) / oldScale
    const cx = (pointerX - offsetX) / oldScale;
    const cy = (pointerY - offsetY) / oldScale;

    // 3) Decide new scale. For example, let's do continuous zoom:
    const zoomFactor = 0.001;
    let newScale = oldScale - event.deltaY * zoomFactor;

    //  - Optionally clamp to minScaleSet or some max:
    newScale = Math.max(this.minScaleAvailable, newScale); // min scale
    newScale = Math.min(1, newScale); // or some arbitrary max

    // 4) Recompute dynamic offset so the content coordinate (cx, cy)
    //    stays in the same screen location (pointerX, pointerY).
    //    pointerX = newOffsetX + newScale * cx
    //    => newOffsetX = pointerX - newScale * cx
    const newOffsetX = pointerX - newScale * cx;
    const newOffsetY = pointerY - newScale * cy;

    // We still want to keep $x, $y as the "base" offset from
    // calculateMineSize. So let's compute the difference to store in *translateX$* / *translateY$*:
    // newOffsetX = $x + translateX => translateX = newOffsetX - $x
    const newTranslateX = newOffsetX - this.$x.value;
    const newTranslateY = newOffsetY - this.$y.value;

    // Update the subjects:

    // Emit to parent so the parent can sync scale & offsets
    // (The parent might do something like x$.next(), y$.next(), etc.)
    this.scrollZoomUpdated.emit({
      scale: newScale,
      x: newTranslateX,
      y: newTranslateY,
    });
  }

  startCanvasDragAndDrop($event: PointerEvent) {
    this.syncBackgroundEditModifier($event.shiftKey);

    if ($event.target !== $event.currentTarget || $event.shiftKey) {
      return;
    }
    console.log($event.target, $event.currentTarget);

    this.enableMoveByDragAndDrop.emit(true);
  }

  startBackgroundEdit(event: PointerEvent): void {
    this.syncBackgroundEditModifier(event.shiftKey);

    if (!event.shiftKey || !this.hasBackgroundImage()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.setHoverOver(null);
    this.isBackgroundDragging = true;
    this.backgroundDragState.active = true;
    this.backgroundDragState.pointerId = event.pointerId;
    this.backgroundDragState.startPointerX = event.clientX;
    this.backgroundDragState.startPointerY = event.clientY;
    this.backgroundDragState.startX = this.backgroundTransform$.value.x;
    this.backgroundDragState.startY = this.backgroundTransform$.value.y;
    this.backgroundDragState.hasChanges = false;
    (event.currentTarget as HTMLElement)?.setPointerCapture?.(event.pointerId);
  }

  moveBackgroundEdit(event: PointerEvent): void {
    this.syncBackgroundEditModifier(event.shiftKey);

    if (
      !this.backgroundDragState.active ||
      this.backgroundDragState.pointerId !== event.pointerId
    ) {
      return;
    }

    if (!event.shiftKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const canvasScale = this.scale.value || 1;
    const deltaX =
      (event.clientX - this.backgroundDragState.startPointerX) / canvasScale;
    const deltaY =
      (event.clientY - this.backgroundDragState.startPointerY) / canvasScale;

    if (
      this.updateBackgroundTransform({
        x: this.backgroundDragState.startX + deltaX,
        y: this.backgroundDragState.startY + deltaY,
        scale: this.backgroundTransform$.value.scale,
      })
    ) {
      this.backgroundDragState.hasChanges = true;
    }
  }

  finishBackgroundEdit(event?: PointerEvent): void {
    if (!this.backgroundDragState.active) {
      this.isBackgroundDragging = false;
      return;
    }

    if (
      event &&
      this.backgroundDragState.pointerId !== null &&
      event.pointerId !== this.backgroundDragState.pointerId
    ) {
      return;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
      (event.currentTarget as HTMLElement)?.releasePointerCapture?.(
        event.pointerId,
      );
    }

    this.backgroundDragState.active = false;
    this.backgroundDragState.pointerId = null;
    this.isBackgroundDragging = false;
    if (this.backgroundDragState.hasChanges) {
      this.emitBackgroundTransform(
        {
          page: this.currentPage$.value,
          background: this.backgroundTransform$.value,
        },
        true,
      );
    }
  }

  disableMoveByDragAndDrop() {
    this.enableMoveByDragAndDrop.emit(false);
  }
  lastRotation = 0;
  @Output() editCustomIndex = new EventEmitter<{
    index: number;
    group: TabloGroupElementConfig;
  }>();
  @Output() requestSourcePhotoPicker = new EventEmitter<{
    item: any;
    callback: (sources: string[]) => void;
  }>();
  @Input() showSpine = false;

  onRotate({ target, transform, dist, beforeRotate }, item: any) {
    this.dndInProgress = true;
    this.resizeSyncService.sizeUpdated.next(item?.id);
    this.frame.rotate = beforeRotate;
    this.lastRotation = dist;
    // get drag event
    target.style.transform = transform;
    snapTransform(target);
  }
  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.finishBackgroundEdit();
    this.structureUpdateQueue.flushPending();
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }
  onRotateEnd({ target, isDrag }, item: any) {
    this.dndInProgress = false;
    this.setHoverOver(item.id);
    const currentStructure = this.structure$.getValue();
    const index = currentStructure.findIndex((x) => x.id === item.id);
    // currentStructure[index].config.rotation =
    //   (currentStructure[index].config.rotation ?? 0) + this.lastRotation;

    currentStructure[index].config.transform = target.style.transform;

    this.structure$.next([...currentStructure]);
  }

  onRotateStart({ set }, item: any) {
    set(this.frame.rotate);
  }

  onDragStart(
    { set, target }: MoveableEventsParameters['dragStart'],
    item: any,
  ) {
    const style = window.getComputedStyle(target);
    const { x, y } = parseTransform(
      target.style.transform ||
        item?.config?.transform ||
        style.transform ||
        '',
    );
    this.frame.translate = [x, y];
    set?.(this.frame.translate);
    console.log({
      frame: this.frame,
    });
    console.log('done');
  }

  async assignPhotos(item: any) {
    this.disabled = true;
    const allGroups = await firstValueFrom(this.sourceGroups$);

    // Check if we need to show the picker dialog
    if (
      allGroups.length > 1 &&
      (!item.config.imagesSource || item.config.imagesSource.length === 0)
    ) {
      // Emit event to request the picker dialog
      this.requestSourcePhotoPicker.emit({
        item,
        callback: async (selectedSources: string[]) => {
          // Update the item's imagesSource with the selected sources
          item.config.imagesSource = selectedSources;

          // Continue with photo assignment using the selected sources
          await this.continueAssignPhotos(item, allGroups);
        },
      });

      // Exit early, the callback will be called when sources are selected
      this.disabled = false;
      return;
    }

    // Continue with the normal flow if we don't need the picker
    await this.continueAssignPhotos(item, allGroups);
  }

  private async continueAssignPhotos(item: any, allGroups: any[]) {
    // If in mockup mode, skip the dialog and use mockup data
    if (this.isMockup) {
      // Create a mockup face-to-person mapping
      const mockupConfig = {};

      // For each member in the list, assign the corresponding mockup photo
      item.config.list.members.forEach((member, index) => {
        if (!member.deleted) {
          const photoId = `mockup/student${(index % 5) + 1}.png`;
          if (member.tabloMemberId) {
            mockupConfig[member.tabloMemberId] = {
              photoId,
              x: 0,
              y: 0,
              width: 100,
              height: 100,
              scale: 1,
              rotation: 0,
            };
          }
        }
      });

      this.disabled = false;
      this.tabloFaceToPerson$.next(mockupConfig);
      this.photosAssigned.emit(mockupConfig);
      return;
    }

    // Normal flow for non-mockup mode
    const ref = this.zalamoDialogService.openFull(
      FaceToLabelPhotographerComponent,
      {
        isBare: true,
      },
    );
    ref.componentInstance.hasCircle = item.config.config.photoAsCircle;
    ref.componentInstance.contextId = this.contextId;
    ref.componentInstance.imagesContext = this.context;
    ref.componentInstance.publicSessionSignupCampaignId =
      this.publicSessionSignupCampaignId;

    const photos = allGroups
      .filter(
        ({ photoGroupId }) =>
          item.config.imagesSource.includes(photoGroupId) ||
          allGroups.length === 1,
      )
      .flatMap(({ images, photoGroupId }) =>
        images.files.map((v) => ({
          ...v,
          groupId: photoGroupId,
        })),
      );

    ref.componentInstance.tabloMembers = item.config.list.members.filter(
      (v) => !v.deleted,
    );
    if (this.tabloFaceToPerson$.value) {
      ref.componentInstance.currentConfig$.next(
        this.tabloFaceToPerson$.getValue(),
      );
    }

    // ref.componentInstance.imageRatio = name.photo.width / name.photo.height;

    await firstValueFrom(ref.afterOpened());
    ref.componentInstance.photos = photos;
    const current = await firstValueFrom(ref.componentInstance.saved);
    ref.close();
    this.disabled = false;
    this.tabloFaceToPerson$.next(current);
    this.photosAssigned.emit(current);
  }

  calculateElementsWithHover(event: PointerEvent): void {
    if (this.dndInProgress) {
      return;
    }

    let nextHoverId = this.hoverOver.value;
    if (event.shiftKey) {
      nextHoverId = null;
    } else {
      const pointElements = document.elementsFromPoint(
        event.clientX,
        event.clientY,
      );
      const matchingElements = pointElements.filter((element) =>
        element.classList.contains('focusStealer'),
      );
      const isOverMoveableControls = pointElements.some((element) =>
        element.getAttribute('class')?.includes('moveable'),
      );

      if (matchingElements.length) {
        if (
          !matchingElements.some(
            (element) => element.getAttribute('data-id') === nextHoverId,
          )
        ) {
          nextHoverId = matchingElements[0].getAttribute('data-id');
        }
      } else if (!isOverMoveableControls) {
        nextHoverId = null;
      }
    }

    if (
      nextHoverId === this.hoverOver.value &&
      event.shiftKey === this.isShiftDown$.value
    ) {
      return;
    }

    this.zone.run(() => {
      this.syncBackgroundEditModifier(event.shiftKey);
      this.setHoverOver(nextHoverId);
      this.cdr.markForCheck();
    });
  }

  imageLoaded(name: any) {}
}
